import { useCallback, useMemo } from 'react';
import {
  fieldAddProductVulcan,
  prequalificationField,
  CONSTANT,
  fieldIsVulcanOrder,
  fieldProvinceVUL,
  vulcanProvinces,
  fieldProductVulcan,
  showProductABBC,
  fieldCityVul,
  fieldNewWirelineService
} from '../../../utils/constant'; 
import { getFieldValue } from '../../utils';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { handleVulcanProductDisable, filterProductsByProvince, isOutletAllowedForTelusInternetBackup } from './utils';

/**
 * Dealership-specific product visibility rules
 * Define which products should be shown for each dealership
 * Use translation keys from vulcanWebform (e.g., vulFibreInternet, vulBusinessWifi)
 * 
 * Dealership name comes from: orderData.orderCreatedBy.Dealership.name
 * 
 * WOW! mobile boutique:
 *   - Show: Fibre Internet, Business Wifi, Mobility
 *   - Hide: Telus Secure Business (TSB), Business Connect (BCON)
 */
const getDealershipProductRules = (locale, dealershipName) => {
  if (!dealershipName) return {};
  
  const translations = locale === 'en-US' ? EN : FR;
  const { vulcanWebform } = translations;
  
  const rules = {
    'wow! mobile boutique': {
      allowedProducts: [
        vulcanWebform.vulFibreInternet,
        vulcanWebform.vulBusinessWifi,
        vulcanWebform.vulMobility
      ]
    }
  };
  
  return rules[dealershipName.toLowerCase()] || {};
};

const CustomerHelpers = ({
  fieldName,
  formFields: rawFormFields,
  fields,
  setResetSection,
  channelName,
  orderData,
  dealershipName,
  salesRepOutlet
}) => {
  const locale = getLocale();
  const formFields = rawFormFields;
  const {
    vulcanWebform: {
      businessWifiVul,
      vulBusinessWifi,
      vulFibreInternet,
      vulBusinessConnect,
      vulTSB,
      vulBusinessOptikTV,
      vulOfficePhone,
      vulWhsia,
      vulCopperInternet,
      vulInternetBackup,
      vulMobility,
      vulFibreOpticDrop
    }
  } = locale === 'en-US' ? EN : FR;
  const {
    KEYS: {
      CHANNELS: { CORP_STORE }
    }
  } = CONSTANT;

  const addProductVulcanValue = getFieldValue(
    fieldAddProductVulcan,
    formFields
  );
  const serviceAddress =
    getFieldValue(fieldProvinceVUL, formFields) || orderData.province_VUL;

  const wlnProducts = [
    vulBusinessOptikTV,
    vulOfficePhone,
    vulWhsia,
    vulCopperInternet,
    vulInternetBackup,
    vulFibreOpticDrop
  ];

  // Products shown in wireline flow only when address is Alberta or British Columbia (incl. AB/BC)
  // e.g. Internet Backup must only appear for AB/BC addresses in wireline
  const productForABBC = [
    vulInternetBackup,
    vulBusinessOptikTV,
    vulOfficePhone
  ];
    
  // This condition use to set isVulcanOrder field required and based on service address
  if (fieldName === fieldIsVulcanOrder) {
    if (!vulcanProvinces.includes(serviceAddress)) {
      formFields[fieldIsVulcanOrder].value = undefined;
      formFields[fieldIsVulcanOrder].isRequired = false;
      return true;
    }
    if (vulcanProvinces.includes(serviceAddress)) {
      formFields[fieldIsVulcanOrder].isRequired = true;
    }
  }

  if (fieldName === fieldProductVulcan) {
    // This condition use to filter the productVulcan field
    if (orderData.isThisVulcanOrder) {
      fields.options = formFields[fieldProductVulcan].options.filter(
        option => !wlnProducts.includes(option.fields.value)
      );
    }
    if(!orderData.isThisVulcanOrder){
      fields.options = formFields[fieldProductVulcan].options.filter(
        option => ![vulWhsia].includes(option.fields.value)
      );
    }
  }

  if (fieldName === fieldAddProductVulcan) {

    // This condition use to set default value for addProductVulcan field for vulcan order
    if (!formFields[fieldAddProductVulcan].value && orderData.isThisVulcanOrder) {
      formFields[fieldAddProductVulcan].value = [
        vulFibreInternet,
        vulBusinessWifi
      ];
      setResetSection(true);
    }

    // This function use to enable and disable addProductVulcan options
    handleVulcanProductDisable({
      formFields,
      businessWifiVul,
      vulFibreInternet,
      vulBusinessWifi,
      orderData
    });

    // Reset all prequalification section fields if no TSB product is selected
    if (addProductVulcanValue && !addProductVulcanValue.includes(vulTSB)) {
      prequalificationField.forEach(resetFieldName => {
        formFields[resetFieldName].value = undefined;
      });
      formFields.isHomeBased.value = undefined;
    }
    
    // This condition use to filter out business connect option from addProductVulcan field for corp store channel
    if (channelName === CORP_STORE) {
      fields.options = formFields[fieldAddProductVulcan].options.filter(
        option => option.fields.value !== vulBusinessConnect
      );
    }

    if (orderData.isThisVulcanOrder) {
      // This condition use to filter the addProductVulcan field
      fields.options = formFields[fieldAddProductVulcan].options.filter(
        option =>
          !wlnProducts.includes(option.fields.value)
      );

      // Filter products based on dealership rules
      if (dealershipName) {
        const dealershipRules = getDealershipProductRules(locale, dealershipName);
        
        if (dealershipRules.allowedProducts && fields.options) {
          const filteredOptions = fields.options.filter(option => {
            const optionValue = option.fields.value;
            const isAllowed = dealershipRules.allowedProducts.includes(optionValue);
            return isAllowed;
          });
    
          fields.options = filteredOptions;
          if (formFields[fieldAddProductVulcan].options) {
            formFields[fieldAddProductVulcan].options = filteredOptions;
          }
        }
      }
    }

    // Wireline (WLN) flow: Internet Backup and other AB/BC products only when Alberta or BC address
    if (!orderData.isThisVulcanOrder) {
      fields.note = '';

      const originalOptions = formFields[fieldAddProductVulcan].options;
      let filteredOptions = [...originalOptions];

      // Internet backup (and Optik TV, Office Phone) only appear for Alberta or British Columbia address
      const isABBCAddress = showProductABBC.includes(serviceAddress);
      if (!isABBCAddress) {
        filteredOptions = filteredOptions.filter(
          option => !productForABBC.includes(option.fields.value)
        );
      }

      // vulFibreOpticDrop: Only display for London/Kitchener/Cambridge/St. Catharines (Ontario) or Laval (Quebec) orders.
      // City and province checks are
      // case-insensitive and accent-insensitive (e.g. accepts "Québec", "quebec", "QC").
      const serviceCity = getFieldValue(fieldCityVul, formFields) || orderData.city_VUL;
      const normalizedServiceCity = (serviceCity || '').trim().toLowerCase();
      const normalizedProvinceKey = (serviceAddress || '').trim().toLowerCase();
      const ontarioFibreEligibleCities = ['london', 'kitchener', 'cambridge', 'st. catharines', 'st catharines', 'saint catharines'];
      const quebecFibreEligibleCities = ['laval'];
      // Accept English/French and short forms of Quebec (with or without accent on "é")
      const quebecProvinceAliases = ['quebec', 'québec', 'que.', 'qc'];
      const isOntarioFibreEligibleCity =
        normalizedProvinceKey === 'ontario' &&
        ontarioFibreEligibleCities.includes(normalizedServiceCity);
      const isQuebecFibreEligibleCity =
        quebecProvinceAliases.includes(normalizedProvinceKey) &&
        quebecFibreEligibleCities.includes(normalizedServiceCity);
      const isFibreOpticDropEligible =
        isOntarioFibreEligibleCity || isQuebecFibreEligibleCity;
      const hasAllowedOutletForTelusInternetBackup = isOutletAllowedForTelusInternetBackup(salesRepOutlet);

      // Hide vulFibreOpticDrop only when address is NOT in an eligible city/province.
      if (!isFibreOpticDropEligible) {
        filteredOptions = filteredOptions.filter(
          option => option.fields.value !== vulFibreOpticDrop
        );
      }

      // Hide vulInternetBackup if NOT allowed outlet
      if (!hasAllowedOutletForTelusInternetBackup) {
        filteredOptions = filteredOptions.filter(
          option => option.fields.value !== vulInternetBackup
        );
      }

      // Remove unwanted Vulcan products
      filteredOptions = filteredOptions.filter(
        option =>
          ![vulWhsia, vulMobility, vulBusinessConnect].includes(
            option.fields.value
          )
      );

      fields.options = filteredOptions;
      formFields[fieldAddProductVulcan].options = filteredOptions;
    }

    // Apply province-based filtering for Eastern provinces
    if (fields.options) {
      fields.options = filterProductsByProvince(
        serviceAddress,
        fields.options,
        locale
      );
      if (formFields[fieldAddProductVulcan].options) {
        formFields[fieldAddProductVulcan].options = fields.options;
      }
    }
  }

  if ( fieldName === fieldNewWirelineService && orderData.isThisVulcanOrder) {
    formFields[fieldName].isRequired = false;
    return true;
  }
  return false;
};

export default CustomerHelpers;
